'use client'

// Vista de cuenta: lo que ve el usuario logueado al clickear su avatar en el
// sidebar. Vive dentro del content de DashboardPage (case 'account'), igual
// que LoginCard — no hay página suelta ni popup.

import React, { useCallback, useState, useSyncExternalStore } from 'react'
import styled from 'styled-components'
import Image from 'next/image'
import { Logout as LogoutIcon, OpenInNew as ExternalIcon, Sync as SyncIcon } from '@mui/icons-material'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { getBridge } from '@/utils/bridge'
import { getLastSyncAt, getSyncStatus, subscribeSyncStatus, syncNow } from '@/utils/sync/engine'

// OJO con globalStyles.js: hay un `div { display:flex; flex-direction:row;
// align-items:flex-start }` global. Sin align-items explícito los hijos no se
// estiran y las filas quedan cortadas a lo ancho.
const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 24px;
`

const Panel = styled.div`
    width: 100%;
    max-width: 520px;
    background: rgba(13, 17, 23, 0.55);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
    animation: fadeIn 0.4s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
`

const Identity = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 18px;
`

const Avatar = styled.img`
    width: 72px;
    height: 72px;
    border-radius: 10px;
    border: 2px solid rgba(99, 102, 241, 0.4);
    box-shadow: 0 0 14px rgba(99, 102, 241, 0.2);
    flex-shrink: 0;
`

const AvatarFallback = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 10px;
    border: 2px solid rgba(99, 102, 241, 0.4);
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.35) 0%, rgba(168, 85, 247, 0.35) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 28px;
    font-weight: 800;
    color: #fff;
    flex-shrink: 0;
`

const Names = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
`

const DisplayName = styled.div`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 22px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const Handle = styled.div`
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.35);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const SteamLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #66c0f4;
    text-decoration: none;
    cursor: pointer;
    opacity: 0.8;

    &:hover {
        opacity: 1;
        text-decoration: underline;
    }

    svg {
        font-size: 14px;
    }
`

const Rows = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
`

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    font-size: 13px;
`

const RowLabel = styled.span`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: rgba(255, 255, 255, 0.4);
`

const RowValue = styled.span`
    color: rgba(255, 255, 255, 0.85);
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const SyncValue = styled(RowValue)`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ $status }) => ($status === 'error' ? '#f87171' : $status === 'syncing' ? '#facc15' : 'rgba(255,255,255,0.85)')};
`

const GhostButton = styled.button`
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.6);
    border-radius: 8px;
    padding: 6px 12px;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        color: #fff;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(99, 102, 241, 0.12);
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`

const LogoutButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px;
    border-radius: 10px;
    background: rgba(255, 75, 75, 0.08);
    border: 1px solid rgba(255, 75, 75, 0.25);
    color: rgba(255, 120, 120, 0.9);
    font-family: 'Chakra Petch', sans-serif;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        color: #fff;
        background: rgba(255, 75, 75, 0.18);
        border-color: rgba(255, 75, 75, 0.5);
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    svg {
        font-size: 18px;
    }
`

const SYNC_LABELS = {
    idle: 'idle',
    syncing: 'sync...',
    synced: 'OK',
    error: 'error',
    disabled: 'off',
}

export default function AccountView() {
    const { t } = useLanguage()
    const { user } = useAuth()
    const [loggingOut, setLoggingOut] = useState(false)
    const [avatarBroken, setAvatarBroken] = useState(false)

    // El engine de sync es un store externo (módulo con listeners), no un
    // context — useSyncExternalStore lo lee sin re-renderizar toda la app.
    const syncStatus = useSyncExternalStore(subscribeSyncStatus, getSyncStatus, () => 'idle')
    const lastSyncAt = useSyncExternalStore(subscribeSyncStatus, getLastSyncAt, () => null)

    const handleLogout = useCallback(async () => {
        setLoggingOut(true)
        try {
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
        } catch {
            // Si el server no responde salimos igual: quedarse trabado con el
            // botón en "saliendo..." es peor. Sin la cookie borrada el landing
            // va a seguir mostrando "Ir al editor" hasta el próximo intento.
        }
        // Al landing público, no a /login: después de cerrar sesión lo natural
        // es volver a la home, no quedar mirando el formulario de entrada.
        // Recarga dura a propósito: limpia todos los contexts en memoria
        // (config, sync) que quedaron atados al usuario que se va.
        window.location.href = '/'
    }, [])

    if (!user) return null

    const name = user.displayName || user.username
    const profileUrl = `https://steamcommunity.com/profiles/${user.steamId}`
    const memberSince = user.createdAt ? new Date(user.createdAt).toLocaleDateString() : null
    const lastSyncLabel = lastSyncAt ? new Date(lastSyncAt).toLocaleTimeString() : t('ACCOUNT_SYNC_NEVER')

    const openProfile = (e) => {
        // En el shell Tauri un <a target=_blank> abriría el perfil DENTRO de
        // la webview de la app; el bridge lo manda al browser del sistema.
        const bridge = getBridge()
        if (bridge?.openExternal) {
            e.preventDefault()
            bridge.openExternal(profileUrl)
        }
    }

    return (
        <Wrapper>
            <Panel>
                <Identity>
                    {user.avatarUrl && !avatarBroken ? (
                        <Avatar src={user.avatarUrl} alt="" onError={() => setAvatarBroken(true)} />
                    ) : (
                        <AvatarFallback>{name.charAt(0).toUpperCase()}</AvatarFallback>
                    )}
                    <Names>
                        <DisplayName title={name}>{name}</DisplayName>
                        {/* El handle solo si aporta algo: cuando no hay URL
                            personalizada en Steam, username es el fallback
                            Player_xxxxxx y repetirlo abajo del nombre sobra. */}
                        {user.username && user.username !== name && (
                            <Handle title={user.username}>@{user.username}</Handle>
                        )}
                        <SteamLink href={profileUrl} target="_blank" rel="noreferrer" onClick={openProfile}>
                            <Image src="/Steam_icon_logo.png" alt="" width={14} height={14} />
                            {t('ACCOUNT_STEAM_PROFILE')}
                            <ExternalIcon />
                        </SteamLink>
                    </Names>
                </Identity>

                <Rows>
                    <Row>
                        <RowLabel>{t('ACCOUNT_STEAMID')}</RowLabel>
                        <RowValue title={user.steamId}>{user.steamId}</RowValue>
                    </Row>
                    {memberSince && (
                        <Row>
                            <RowLabel>{t('ACCOUNT_MEMBER_SINCE')}</RowLabel>
                            <RowValue>{memberSince}</RowValue>
                        </Row>
                    )}
                    <Row>
                        <RowLabel>{t('ACCOUNT_SYNC')}</RowLabel>
                        <SyncValue $status={syncStatus}>
                            {SYNC_LABELS[syncStatus] ?? syncStatus} · {lastSyncLabel}
                            <GhostButton onClick={() => syncNow()} disabled={syncStatus === 'syncing'}>
                                <SyncIcon style={{ fontSize: 13, verticalAlign: '-2px', marginRight: 4 }} />
                                {t('ACCOUNT_SYNC_NOW')}
                            </GhostButton>
                        </SyncValue>
                    </Row>
                </Rows>

                <LogoutButton onClick={handleLogout} disabled={loggingOut}>
                    <LogoutIcon />
                    {t('ACCOUNT_LOGOUT')}
                </LogoutButton>
            </Panel>
        </Wrapper>
    )
}
